import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import * as taskboardService from '../services/taskboardService';

interface WorkflowColumnManagerProps {
  columns: any[];
  onClose: () => void;
  onUpdate: () => void;
}

export function WorkflowColumnManager({ columns, onClose, onUpdate }: WorkflowColumnManagerProps): React.ReactElement {
  const { token } = useAuth();
  const [localColumns, setLocalColumns] = useState(columns);
  const [newColumnName, setNewColumnName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');

  async function handleAddColumn() {
    if (!token || !newColumnName.trim()) return;
    setError('');
    try {
      await taskboardService.createColumn(newColumnName.trim(), token);
      setNewColumnName('');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add column');
    }
  }

  async function handleRename(id: string) {
    if (!token || !editName.trim()) return;
    setError('');
    try {
      await taskboardService.updateColumn(id, editName.trim(), token);
      setEditingId(null);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to rename column');
    }
  }

  async function handleDelete(id: string) {
    if (!token) return;
    setError('');
    try {
      await taskboardService.deleteColumn(id, token);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete column');
    }
  }

  async function handleMoveUp(index: number) {
    if (index === 0 || !token) return;
    const newOrder = [...localColumns];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalColumns(newOrder);
    try {
      await taskboardService.reorderColumns(newOrder.map(c => c.id), token);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reorder columns');
    }
  }

  async function handleMoveDown(index: number) {
    if (index >= localColumns.length - 1 || !token) return;
    const newOrder = [...localColumns];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setLocalColumns(newOrder);
    try {
      await taskboardService.reorderColumns(newOrder.map(c => c.id), token);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reorder columns');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md m-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Manage Columns</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400 p-2 rounded">{error}</div>
          )}

          {localColumns.map((col, index) => (
            <div key={col.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => handleMoveUp(index)} disabled={index === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                </button>
                <button onClick={() => handleMoveDown(index)} disabled={index >= localColumns.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>

              {editingId === col.id ? (
                <div className="flex-1 flex gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(col.id); }}
                  />
                  <button onClick={() => handleRename(col.id)} className="text-indigo-600 text-sm hover:text-indigo-800">Save</button>
                  <button onClick={() => setEditingId(null)} className="text-gray-400 text-sm hover:text-gray-600">Cancel</button>
                </div>
              ) : (
                <>
                  <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{col.name}</span>
                  {col.is_default && <span className="text-xs text-gray-400 dark:text-gray-500">Default</span>}
                  <button
                    onClick={() => { setEditingId(col.id); setEditName(col.name); }}
                    className="text-gray-400 hover:text-indigo-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  </button>
                  {!col.is_default && (
                    <button
                      onClick={() => handleDelete(col.id)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add new column */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex gap-2">
            <input
              type="text"
              value={newColumnName}
              onChange={e => setNewColumnName(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              placeholder="New column name"
              onKeyDown={e => { if (e.key === 'Enter') handleAddColumn(); }}
            />
            <button
              onClick={handleAddColumn}
              disabled={!newColumnName.trim()}
              className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkflowColumnManager;
